/**
 * Pure scope resolution for department-based access management.
 *
 * A user's effective access comes from three independent sources:
 *   1. direct user-level grants,
 *   2. the parent scope hierarchy modelled in `scopeHierarchy.js`
 *      (`supervisor` -> six children, `semantic` -> two children),
 *   3. the scopes carried by every department the user belongs to.
 *
 * This module merges those sources, annotates each scope with where it came
 * from, and derives the checkbox state `ScopeTree` renders. It is pure: no
 * React, no API calls, no module-level state. Every function tolerates
 * malformed department payloads rather than throwing (Req 10.7).
 *
 * Determinism rules (Req 8.6, 10.5):
 *   - scopes are ordered by their index in `ALL_SCOPES`; scopes outside
 *     `ALL_SCOPES` are appended after, ordered by code-unit comparison,
 *   - department names are ordered by `(name, id)` using plain `<`
 *     comparison rather than `localeCompare`, so ordering never varies with
 *     the runtime locale,
 *   - no ordering reads input array order, which is what makes results
 *     independent of how the caller happened to order its inputs.
 */

import {
  ALL_SCOPES,
  getParentScope,
  isInheritedScope,
  resolveEffectiveScopes,
} from "./scopeHierarchy";

/**
 * Scopes a department may never grant. `admin` is a standalone privileged
 * scope; `supervisor` and `semantic` are hierarchy parents granted at the
 * user level only.
 */
const NON_GRANTABLE_SCOPES = ["admin", "supervisor", "semantic"];

/**
 * The eleven scopes selectable in the Department dialog, derived from
 * `ALL_SCOPES` so the display order stays identical to `ScopeTree`'s and
 * cannot drift as scopes are added or reordered.
 *
 * @type {string[]}
 */
export const GRANTABLE_DEPARTMENT_SCOPES = ALL_SCOPES.filter(
  (scope) => !NON_GRANTABLE_SCOPES.includes(scope)
);

/**
 * Orders two scopes by their position in `ALL_SCOPES`. Unknown scopes sort
 * after every known scope, and among themselves by code unit.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function compareScopes(a, b) {
  const indexA = ALL_SCOPES.indexOf(a);
  const indexB = ALL_SCOPES.indexOf(b);

  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;

  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Returns a new array of the given scopes in canonical display order.
 *
 * @param {Iterable<string>} scopes
 * @returns {string[]}
 */
function sortScopes(scopes) {
  return Array.from(scopes).sort(compareScopes);
}

/**
 * Orders two department source records by `(name, id)` with plain `<`
 * comparison. Numeric ids compare numerically; anything else falls back to
 * its string form so the comparison stays total.
 *
 * @param {{ name: string, id: * }} a
 * @param {{ name: string, id: * }} b
 * @returns {number}
 */
function compareDepartmentSources(a, b) {
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;

  const bothNumeric =
    typeof a.id === "number" &&
    typeof b.id === "number" &&
    Number.isFinite(a.id) &&
    Number.isFinite(b.id);

  if (bothNumeric) {
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
  }

  const idA = String(a.id);
  const idB = String(b.id);
  if (idA === idB) return 0;
  return idA < idB ? -1 : 1;
}

/**
 * Reads a department's scopes defensively.
 *
 * Returns an empty array when the department is null, undefined, not an
 * object, or has a missing or non-array `scopes` field, and drops any
 * non-string item from an otherwise valid array (Req 10.7).
 *
 * @param {*} department - A department payload of unknown shape.
 * @returns {string[]} The department's string scopes, possibly empty.
 */
export function readDepartmentScopes(department) {
  if (!department || typeof department !== "object") return [];
  if (!Array.isArray(department.scopes)) return [];
  return department.scopes.filter((scope) => typeof scope === "string");
}

/**
 * Reads a department's display name defensively, falling back to
 * `Department #{id}` when the name is missing, blank, or not a string.
 *
 * @param {*} department - A department payload of unknown shape.
 * @returns {string} A non-empty display name.
 */
export function readDepartmentName(department) {
  const id =
    department && typeof department === "object" && department.id !== undefined && department.id !== null
      ? department.id
      : "?";

  if (!department || typeof department !== "object") return `Department #${id}`;

  const name = typeof department.name === "string" ? department.name.trim() : "";
  return name || `Department #${id}`;
}

/**
 * The union of every department's scopes, expanded through the parent
 * hierarchy, de-duplicated, and ordered canonically. This is the value
 * `ScopeTree` receives as its `inheritedScopes` prop.
 *
 * @param {*} departments - An array of department payloads, or anything else.
 * @returns {string[]} The department-inherited scope set.
 */
export function getInheritedDepartmentScopes(departments) {
  const list = Array.isArray(departments) ? departments : [];
  const union = new Set();

  for (const department of list) {
    for (const scope of resolveEffectiveScopes(readDepartmentScopes(department))) {
      union.add(scope);
    }
  }

  return sortScopes(union);
}

/**
 * Maps each department-inherited scope to the names of the departments that
 * grant it. A department that grants a hierarchy parent is credited with
 * that parent's children too, so every scope in
 * `getInheritedDepartmentScopes` has at least one named source.
 *
 * Names are de-duplicated and ordered by `(name, id)`. This is the value
 * `ScopeTree` receives as its `inheritedSources` prop.
 *
 * @param {*} departments - An array of department payloads, or anything else.
 * @returns {{ [scope: string]: string[] }} Scope to granting department names.
 */
export function getDepartmentScopeSources(departments) {
  const list = Array.isArray(departments) ? departments : [];
  const byScope = new Map();

  for (const department of list) {
    const scopes = resolveEffectiveScopes(readDepartmentScopes(department));
    if (scopes.length === 0) continue;

    const source = {
      name: readDepartmentName(department),
      id: department && typeof department === "object" ? department.id : undefined,
    };

    for (const scope of scopes) {
      if (!byScope.has(scope)) byScope.set(scope, []);
      byScope.get(scope).push(source);
    }
  }

  const sources = {};

  for (const scope of sortScopes(byScope.keys())) {
    const ordered = byScope.get(scope).slice().sort(compareDepartmentSources);
    const names = [];

    for (const { name } of ordered) {
      if (names[names.length - 1] !== name) names.push(name);
    }

    sources[scope] = names;
  }

  return sources;
}

/**
 * Formats the human-readable source label for one effective scope entry.
 *
 * | Situation             | Label                                     |
 * |-----------------------|-------------------------------------------|
 * | direct only           | `chat (direct)`                           |
 * | direct via hierarchy  | `sales (direct, via supervisor)`          |
 * | one department        | `sales (from: Sales Operations)`           |
 * | several departments   | `sales (from: Field Force, Sales Ops)`     |
 * | direct and department | `sales (direct, from: Sales Operations)`   |
 *
 * @param {{ scope: string, direct?: boolean, impliedBy?: string|null, departments?: string[] }} entry
 * @returns {string} The label, or the bare scope when there is no source.
 */
export function formatScopeSourceLabel(entry) {
  if (!entry || typeof entry !== "object") return "";

  const scope = typeof entry.scope === "string" ? entry.scope : "";
  const departments = Array.isArray(entry.departments) ? entry.departments : [];
  const parts = [];

  if (entry.direct) {
    parts.push(entry.impliedBy ? `direct, via ${entry.impliedBy}` : "direct");
  }

  if (departments.length > 0) {
    parts.push(`from: ${departments.join(", ")}`);
  }

  if (parts.length === 0) return scope;
  return `${scope} (${parts.join(", ")})`;
}

/**
 * Resolves a user's effective access from their direct grants and their
 * department memberships (Req 10.1).
 *
 * The returned list is the de-duplicated union of the hierarchy-expanded
 * direct scopes and the hierarchy-expanded scopes of every department: each
 * scope appears exactly once, annotated with its sources, ordered
 * canonically. Empty inputs yield an empty list.
 *
 * @param {*} directScopes - The user's direct grants, or anything else.
 * @param {*} departments - The user's departments, or anything else.
 * @returns {Array<{ scope: string, direct: boolean, impliedBy: string|null, departments: string[], label: string }>}
 */
export function resolveEffectiveScopeList(directScopes, departments) {
  const explicitDirect = Array.isArray(directScopes)
    ? directScopes.filter((scope) => typeof scope === "string")
    : [];
  const explicitDirectSet = new Set(explicitDirect);
  const expandedDirectSet = new Set(resolveEffectiveScopes(explicitDirect));

  const departmentSources = getDepartmentScopeSources(departments);

  const allScopes = new Set([
    ...expandedDirectSet,
    ...Object.keys(departmentSources),
  ]);

  return sortScopes(allScopes).map((scope) => {
    const direct = expandedDirectSet.has(scope);
    const impliedBy =
      direct && !explicitDirectSet.has(scope) ? getParentScope(scope) : null;

    const entry = {
      scope,
      direct,
      impliedBy,
      departments: departmentSources[scope] || [],
    };

    return { ...entry, label: formatScopeSourceLabel(entry) };
  });
}

/**
 * The explicitly granted subset of a resolved list: entries that are direct
 * and not implied by a parent scope. Feeding this back into
 * `resolveEffectiveScopeList` with the same departments reproduces an
 * equivalent list (Req 10.6), because the implied children are regenerated
 * by the hierarchy expansion.
 *
 * @param {*} entries - The result of `resolveEffectiveScopeList`, or anything else.
 * @returns {string[]} The explicit direct scopes, ordered canonically.
 */
export function selectDirectScopes(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const direct = new Set();

  for (const entry of list) {
    if (!entry || typeof entry !== "object") continue;
    if (typeof entry.scope !== "string") continue;
    if (!entry.direct) continue;
    if (entry.impliedBy) continue;
    direct.add(entry.scope);
  }

  return sortScopes(direct);
}

/**
 * Derives one scope row's checkbox state for `ScopeTree`.
 *
 * A scope is locked when it is inherited from either source: from a directly
 * granted parent scope, or from a department membership. Locked scopes render
 * checked, disabled, and badged "inherited" (Req 9.1, 9.5). The caller ORs the
 * returned `disabled` with its own `disabled` prop.
 *
 * @param {{ scope: string, directScopes?: string[], inheritedDepartmentScopes?: string[] }} params
 * @returns {{ checked: boolean, disabled: boolean, inherited: boolean, hierarchyInherited: boolean, departmentInherited: boolean }}
 */
export function resolveScopeControlState({
  scope,
  directScopes,
  inheritedDepartmentScopes,
} = {}) {
  const direct = Array.isArray(directScopes) ? directScopes : [];
  const departmentScopes = Array.isArray(inheritedDepartmentScopes)
    ? inheritedDepartmentScopes
    : [];

  const hierarchyInherited = isInheritedScope(scope, direct);
  const departmentInherited = departmentScopes.includes(scope);
  const inherited = hierarchyInherited || departmentInherited;

  return {
    checked: inherited || direct.includes(scope),
    disabled: inherited,
    inherited,
    hierarchyInherited,
    departmentInherited,
  };
}
